import React, { createContext, useContext } from "react";
import {
  getData,
  patchData,
  postData,
  deleteData,
} from "../utilities/postData";
import { useDualMsal } from "./MsalContext";

// Exported so tooling can render a page against a stub — the provider itself
// pulls in MSAL and live requests, which a static render cannot have.
export const DataProviderContext = createContext();

export const useData = () => {
  return useContext(DataProviderContext);
};

export function DataProvider({ children }) {
  const { currentUser, getToken, isAuthenticated } = useDualMsal();
  const getUserState = async (userId) => {
    const token = await getToken();
    return getData({
      endpoint: "user" + (userId ? "/" + userId : ""),
      accessToken: token,
    });
  };

  // Admin participant LIST — one row per enrollment. Identity merged
  // from the users container; assignments merged against the study's
  // sessions template. Use this for any participants-shaped admin view
  // (the Participants tab, the dashboard cards). For staff identity use
  // getUsersStatuses().
  const getParticipants = async () => {
    const token = await getToken();
    return getData({
      endpoint: "participants",
      accessToken: token,
    });
  };

  const getUsersStatuses = async () => {
    const token = await getToken();
    return getData({
      endpoint: "users",
      accessToken: token,
    });
  };

  const setUserState = async (u) => {
    const suffix = u.id && u.id !== currentUser?.oid ? "/" + u.id : "";
    const token = await getToken();
    return patchData({
      endpoint: "user" + suffix,
      accessToken: token,
      data: u,
    });
  };

  const postTaskData = async (data) => {
    const token = await getToken();
    // Add participantId to the call for caching purposes
    return postData({
      endpoint: "post-data",
      accessToken: token,
      participantId: currentUser?.oid, // Pass the current user's OID as participantId
      data: data,
    });
  };

  // Update getSubmissionMetrics to accept an optional site parameter
  const getSubmissionMetrics = async (site = null) => {
    const token = await getToken();
    const params = {};
    if (site && site !== "All Sites") {
      params.site = site; // Add site to parameters if it's not "All Sites"
    }
    return getData({
      endpoint: "submission-metrics",
      accessToken: token,
      parameters: params, // Pass parameters to getData
    });
  };

  const retrySubmission = async (b2cId) => {
    const token = await getToken();
    return postData({
      endpoint: "retry-submission",
      accessToken: token,
      data: { b2c_id: b2cId },
    });
  };

  // Re-send archived submissions (our safe-store copy) to REDCap. No filter
  // = everything non-durable.
  const repushArchive = async ({ b2cId, studyId, recordId, scope, dryRun, heldId } = {}) => {
    const token = await getToken();
    return postData({
      endpoint: "repush-archive",
      accessToken: token,
      data: { b2c_id: b2cId, study_id: studyId, recordId, scope, dryRun, heldId },
    });
  };

  // initializeRegistration function removed as it's no longer needed.
  // Validation happens in ParticipantRegister.js via API call now.

  const completeRegistration = async (newUserId, account) => {
    if (!newUserId) {
      console.error(
        "Cannot complete registration: missing new user ID (B2C OID)."
      );
      return false;
    }

    const b2cAuthToken = await getToken(); // Get the current B2C auth token

    if (!b2cAuthToken) {
      console.error(
        "Cannot complete registration: failed to get B2C authentication token."
      );
      return false;
    }

    // Check if this is a QR enrollment flow
    const qrEnrollmentData = localStorage.getItem("qr_enrollment_data");
    const qrUserId = localStorage.getItem("qr_user_id");

    if (qrEnrollmentData && qrUserId) {
      // QR enrollment flow - call complete-enrollment endpoint
      console.log("Completing QR enrollment flow");

      const enrollmentData = {
        user_id: qrUserId,
        b2c_id: newUserId,
      };

      return postData({
        endpoint: "complete-enrollment",
        accessToken: b2cAuthToken,
        data: enrollmentData,
      })
        .then((response) => {
          console.log("QR enrollment completion successful.");
          return true;
        })
        .catch((error) => {
          console.error("QR enrollment completion failed:", error);

          // Handle 409 (already enrolled) as success
          // This happens when user logs in after consent but is already enrolled
          if (error.status === 409) {
            console.log(
              "User already enrolled in this study - proceeding to dashboard"
            );
            return true; // Treat as success so they can access their dashboard
          }

          return false;
        });
    }

    // Legacy invite flow - check for invite token
    const inviteToken = localStorage.getItem("invite_token");

    if (!inviteToken) {
      console.error(
        "Cannot complete registration: missing invite token in localStorage."
      );
      localStorage.removeItem("registration_data");
      return false;
    }

    // Legacy invite flow - call b2c-signup-complete endpoint
    console.log("Completing legacy invite flow");

    const finalizeData = {
      invite_token: inviteToken,
      new_user_id: newUserId,
    };

    // Add optional details if available from the B2C account object
    if (account.name) {
      finalizeData.new_user_name = account.name;
    }

    if (account.username) {
      finalizeData.new_user_email = account.username;
    }

    if (account.policy) {
      finalizeData.policy = account.policy;
    }

    return postData({
      endpoint: "b2c-signup-complete",
      accessToken: b2cAuthToken,
      data: finalizeData,
    })
      .then((response) => {
        console.log("Legacy registration completion successful.");
        return true;
      })
      .catch((error) => {
        console.error("Legacy registration completion failed:", error);
        return false;
      });
  };

  // Get rewards for a specific enrolment. participantId is the
  // composite `${b2c_id}_${study_id}` written on Participants/rewards
  // docs since v1.3.0. Callers should pass participantData.id (from
  // /api/participant on the participant side, or the participant row's
  // .id on the admin side).
  const getRewardsBalance = async (participantId) => {
    if (!participantId) {
      console.error(
        "Cannot get rewards: missing participantId (composite enrolment id)."
      );
      return { balance: 0, redeemedVouchers: [] };
    }
    const token = await getToken();
    if (!token) {
      console.error("Cannot get rewards: failed to get token.");
      return { balance: 0, redeemedVouchers: [] };
    }
    try {
      const response = await getData({
        endpoint: `participant/${participantId}/rewards`,
        accessToken: token,
      });
      return response || { balance: 0, redeemedVouchers: [] };
    } catch (error) {
      console.error(
        `Error fetching rewards for participant ${participantId}:`,
        error
      );
      if (error.code === 404 || error.status === 404) {
        return { balance: 0, redeemedVouchers: [] };
      }
      return { balance: 0, redeemedVouchers: [] };
    }
  };

  // Redeem rewards for a specific enrolment. participantId tells the
  // backend WHICH enrolment's balance to drain — multi-study
  // participants have one balance per study, so the wrong omission
  // here would mix studies.
  const redeemRewards = async (participantId) => {
    if (!participantId) {
      console.error("Cannot redeem rewards: missing participantId.");
      throw new Error("Missing participantId for redemption.");
    }
    const token = await getToken();
    if (!token) {
      throw new Error("Failed to get authentication token.");
    }
    try {
      return await postData({
        // Love2Shop takes 15–30 s per order, past the 30 s default.
        timeout: 90000,
        endpoint: `redeem-rewards`,
        accessToken: token,
        data: { participantId },
      });
    } catch (error) {
      console.error("Error redeeming rewards:", error);
      throw error;
    }
  };

  // Adjust reward balance for a specific enrolment (Admin/PI only).
  const adjustRewardBalance = async (
    participantId,
    adjustmentAmountPence,
    reason
  ) => {
    if (
      !participantId ||
      typeof adjustmentAmountPence !== "number" ||
      !reason
    ) {
      console.error("Invalid parameters for adjustRewardBalance");
      throw new Error("Invalid parameters provided for reward adjustment.");
    }
    const token = await getToken();
    if (!token) {
      throw new Error("Failed to get authentication token.");
    }
    try {
      return await postData({
        endpoint: `participant/${participantId}/adjust-reward`,
        accessToken: token,
        data: {
          adjustmentAmount: adjustmentAmountPence,
          reason,
        },
      });
    } catch (error) {
      console.error(
        `Error adjusting rewards for participant ${participantId}:`,
        error
      );
      throw error;
    }
  };

  // Add a bonus reward for a specific task within a specific enrolment.
  // participantId is the composite ${b2c_id}_${study_id} so the bonus
  // lands on the right per-enrolment rewards doc.
  const addBonusReward = async (
    participantId,
    sessionCode,
    taskParam,
    bonusAmountPence
  ) => {
    if (!currentUser?.oid) {
      throw new Error("Cannot add bonus reward: user not authenticated.");
    }
    if (
      !participantId ||
      !sessionCode ||
      !taskParam ||
      !bonusAmountPence ||
      !Number.isSafeInteger(bonusAmountPence) ||
      bonusAmountPence <= 0
    ) {
      throw new Error("Invalid parameters for performance bonus reward.");
    }
    const token = await getToken();
    if (!token) {
      throw new Error("Cannot add bonus reward: failed to get token.");
    }
    try {
      const response = await postData({
        endpoint: `add-reward-bonus`,
        accessToken: token,
        participantId,
        data: {
          participantId,
          sessionCode,
          taskParam,
          bonusAmount: bonusAmountPence,
        },
      });
      console.log(`Bonus reward reported for ${sessionCode}:${taskParam}`);
      return response;
    } catch (error) {
      console.error(
        `Error reporting bonus reward for ${sessionCode}:${taskParam}:`,
        error
      );
      throw error;
    }
  };

  // Get all PO references (PI only)
  const getPOReferences = async () => {
    const token = await getToken();
    return getData({
      endpoint: "purchase-order-references",
      accessToken: token,
    });
  };

  // Get current active PO reference (PI only)
  const getCurrentPOReference = async () => {
    const token = await getToken();
    return getData({
      endpoint: "purchase-order-references/current",
      accessToken: token,
    });
  };

  // Create new PO reference (PI only)
  const createPOReference = async (purchaseOrderReference, description) => {
    const token = await getToken();
    return postData({
      endpoint: "purchase-order-references",
      accessToken: token,
      data: {
        purchaseOrderReference,
        description,
      },
    });
  };

  // Update current PO reference (PI only)
  const updatePOReference = async (purchaseOrderReference, description) => {
    const token = await getToken();
    return postData({
      endpoint: "purchase-order-references",
      accessToken: token,
      data: {
        purchaseOrderReference,
        description,
      },
    });
  };

  // Get voucher analytics (Admin/PI only)
  const getVoucherAnalytics = async () => {
    const token = await getToken();
    return getData({
      endpoint: "voucher-analytics",
      accessToken: token,
    });
  };

  const getStudies = async () => {
    const token = await getToken();
    return getData({
      endpoint: "studies",
      accessToken: token,
    });
  };

  const createStudy = async (studyData) => {
    const token = await getToken();
    return postData({
      endpoint: "studies",
      data: studyData,
      accessToken: token,
    });
  };

  const updateStudy = async (studyData) => {
    const token = await getToken();
    return patchData({
      endpoint: "studies",
      data: studyData,
      accessToken: token,
    });
  };

  const validateQRRegistration = async (qrData) => {
    // Public endpoint - no auth token required
    return postData({
      endpoint: "qr-registration",
      data: qrData,
    });
  };

  const processConsentReturn = async (consentData) => {
    // Public endpoint - no auth token required
    return postData({
      endpoint: "consent-return",
      data: consentData,
    });
  };

  const uploadPIS = async (file) => {
    const token = await getToken();

    // Create FormData for file upload
    const formData = new FormData();
    formData.append("file", file);

    // Use fetch directly for multipart/form-data
    const response = await fetch("/api/upload-pis", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Upload failed");
    }

    return response.json();
  };

  const uploadLandingImage = async (file) => {
    const token = await getToken();

    // Create FormData for file upload
    const formData = new FormData();
    formData.append("file", file);

    // Use fetch directly for multipart/form-data
    const response = await fetch("/api/upload-landing-image", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Upload failed");
    }

    return response.json();
  };

  // Tasks CRUD operations
  const getTasks = async () => {
    const token = await getToken();
    return getData({
      endpoint: "tasks",
      accessToken: token,
    });
  };

  const createTask = async (taskData) => {
    const token = await getToken();
    return postData({
      endpoint: "tasks",
      accessToken: token,
      data: taskData,
    });
  };

  const updateTask = async (taskData) => {
    const token = await getToken();
    return patchData({
      endpoint: "tasks",
      accessToken: token,
      data: taskData,
    });
  };

  const deleteTask = async (taskId) => {
    const token = await getToken();
    return deleteData({
      endpoint: `tasks?id=${taskId}`,
      accessToken: token,
    });
  };

  // Links CRUD operations
  const getLinks = async (studyId, pharmacy = null, drug = null) => {
    const token = await getToken();
    let endpoint = `links?study_id=${studyId}`;

    if (pharmacy && drug) {
      endpoint += `&pharmacy=${encodeURIComponent(
        pharmacy
      )}&drug=${encodeURIComponent(drug)}`;
    }

    return getData({
      endpoint,
      accessToken: token,
    });
  };

  const generateLinks = async (
    studyId,
    pharmacy,
    drug,
    count,
    after = 0,
    isTest = false
  ) => {
    const token = await getToken();
    return getData({
      endpoint: `links?study_id=${studyId}&pharmacy=${encodeURIComponent(
        pharmacy
      )}&drug=${encodeURIComponent(
        drug
      )}&count=${count}&after=${after}&createIfMissing=true&isTest=${
        isTest ? "true" : "false"
      }`,
      accessToken: token,
    });
  };

  // Bulk generation from pasted pharmacy/drug/count rows. The server
  // validates the whole batch before minting anything and rejects with
  // { problems: [{ line, message }] } if any row is wrong.
  const generateLinksBulk = async (studyId, rows, isTest = false) => {
    const token = await getToken();
    return postData({
      endpoint: "links/bulk",
      accessToken: token,
      data: { study_id: studyId, rows, isTest },
    });
  };

  // Codes are computed from the issued ranges rather than stored, so they
  // are read back a page at a time instead of listed. Optional
  // pharmacy/drug/from/to narrow it to one batch's span.
  const exportLinks = async (studyId, options = {}) => {
    const token = await getToken();
    const params = new URLSearchParams({ study_id: studyId });
    ["offset", "limit", "pharmacy", "drug", "from", "to", "mark_printed"].forEach((key) => {
      if (options[key] !== undefined && options[key] !== null) {
        params.set(key, options[key]);
      }
    });
    return getData({
      endpoint: `links/export?${params.toString()}`,
      accessToken: token,
    });
  };

  // Every code in a span, following the paging until it runs out.
  const exportAllLinks = async (studyId, options = {}) => {
    const codes = [];
    let offset = 0;
    // Bounded so a bug upstream cannot spin here forever.
    for (let page = 0; page < 200; page++) {
      const result = await exportLinks(studyId, { ...options, offset });
      codes.push(...(result.codes || []));
      if (result.next_offset === null || result.next_offset === undefined) break;
      offset = result.next_offset;
    }
    return codes;
  };

  const createLink = async (linkData) => {
    const token = await getToken();
    return postData({
      endpoint: "links",
      accessToken: token,
      data: linkData,
    });
  };

  const deleteLink = async (hash) => {
    const token = await getToken();
    return deleteData({
      endpoint: `links?hash=${hash}`,
      accessToken: token,
    });
  };

  // Resolve hash to study data (public)
  const resolveLink = async (hash) => {
    return getData({
      endpoint: `links?hash=${hash}`,
    });
  };

  // Participant data (for logged-in participant)
  const getParticipantData = async (studyId = null) => {
    const token = await getToken();
    const endpoint = studyId
      ? `participant?study_id=${studyId}`
      : "participant";
    return getData({
      endpoint,
      accessToken: token,
    });
  };

  // Admin/PI write against a named participant. Distinct from
  // setParticipantState, which is self-service and resolves the record from
  // the caller's own token — using that from the admin screens wrote to the
  // wrong record, or to none (#29430).
  const setParticipantParticipation = async ({ b2c_id, study_id, ...fields }) => {
    const token = await getToken();
    return patchData({
      endpoint: "participants",
      accessToken: token,
      data: { b2c_id, study_id, ...fields },
    });
  };

  const setParticipantState = async (participantData) => {
    const token = await getToken();
    return patchData({
      endpoint: "participant",
      accessToken: token,
      data: participantData,
    });
  };

  // Email template editing (admin/PI). Templates are platform-level —
  // {{studyName}} carries the per-study difference (#29396).
  const getEmailTemplates = async () => {
    const token = await getToken();
    return getData({ endpoint: "email-templates", accessToken: token });
  };

  const updateEmailTemplate = async (template) => {
    const token = await getToken();
    return patchData({
      endpoint: "email-templates",
      accessToken: token,
      data: template,
    });
  };

  // Renders the given draft with server-side sample data and emails it to
  // the logged-in admin. Never saves.
  const sendTestEmailTemplate = async (template) => {
    const token = await getToken();
    return postData({
      endpoint: "email-templates/test",
      accessToken: token,
      data: template,
    });
  };

  // Get all enrollments for current user
  const getParticipantEnrollments = async () => {
    const token = await getToken();
    return getData({
      endpoint: "participant-enrollments",
      accessToken: token,
    });
  };

  // Complete an enrolment for the already-authenticated user, keyed by the
  // consent record id the consent platform returned. This is the returning-
  // participant half of per-study re-consent: same endpoint the first-time
  // flow uses after B2C signup, so the participant record, conditions relay
  // and QR consumption are identical — there is no consent-free enrolment
  // path any more.
  const completeEnrollmentForCurrentUser = async (user_id) => {
    const token = await getToken();
    return postData({
      endpoint: "complete-enrollment",
      accessToken: token,
      data: { user_id, b2c_id: currentUser?.oid },
    });
  };

  // Set task or session state (rapid atomic updates)
  const setTaskState = async (
    session_id,
    state,
    task = null,
    study_id = null,
    participant_id = null
  ) => {
    const token = await getToken();
    return postData({
      endpoint: "task-state",
      accessToken: token,
      data: { session_id, task, state, study_id, participant_id },
    });
  };

  // Task assignments (for logged-in participant)
  const getTaskAssignments = async (participantId) => {
    const token = await getToken();
    return getData({
      endpoint: `task-assignments?participant_id=${participantId}`,
      accessToken: token,
    });
  };

  return (
    <DataProviderContext.Provider
      value={{
        currentUser,
        getUserState,
        setUserState,
        getUsersStatuses,
        getParticipants,
        postTaskData,
        getSubmissionMetrics,
        retrySubmission,
        repushArchive,
        isAuthenticated,
        // initializeRegistration removed
        completeRegistration,
        getRewardsBalance,
        redeemRewards,
        adjustRewardBalance,
        addBonusReward, // Add addBonusReward to context value
        getPOReferences,
        getCurrentPOReference,
        createPOReference,
        updatePOReference,
        getVoucherAnalytics,
        getStudies,
        createStudy,
        updateStudy,
        validateQRRegistration,
        processConsentReturn,
        uploadPIS,
        uploadLandingImage,
        getTasks,
        createTask,
        updateTask,
        deleteTask,
        getLinks,
        generateLinks,
        generateLinksBulk,
        exportLinks,
        exportAllLinks,
        createLink,
        deleteLink,
        resolveLink,
        getParticipantData,
        setParticipantState,
        setParticipantParticipation,
        setTaskState,
        getTaskAssignments,
        getParticipantEnrollments,
        completeEnrollmentForCurrentUser,
        getEmailTemplates,
        updateEmailTemplate,
        sendTestEmailTemplate,
      }}
    >
      {children}
    </DataProviderContext.Provider>
  );
}
